import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  ArrowRight,
  Clock,
  Copy,
  Share2,
  MessageSquare,
  Heart,
  Repeat2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

/**
 * PostShare — publicly accessible (no wallet required) single-post view.
 * URL: /post/:postId — paired with backend OG endpoint /api/share/post/:id
 * so links pasted on X/Discord/Telegram unfurl with a rich preview card.
 */
export default function PostShare() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/posts/${postId}`);
        if (!res.ok) {
          if (active) setPost(null);
          return;
        }
        const data = await res.json();
        if (active) setPost(data || null);
      } catch {
        if (active) setPost(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [postId]);

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/post/${postId}`
      : `/post/${postId}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it on X");
    } catch {
      toast.error("Copy failed — long-press the URL bar to copy");
    }
  }

  function shareToX() {
    const author = post?.user?.username || (post?.user?.stx_address ? `user_${post.user.stx_address.slice(0, 8)}` : "STXOSHI user");
    const preview = (post?.content || "").split("\n")[0].slice(0, 140);
    const text = preview
      ? `"${preview}" — ${author} on @stxoshi_TOK`
      : `Post by ${author} on @stxoshi_TOK`;
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (loading) {
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center text-muted-foreground"
        data-testid="post-share-loading"
      >
        Loading post…
      </div>
    );
  }
  if (!post) {
    return (
      <div
        className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 px-6 text-center"
        data-testid="post-share-not-found"
      >
        <h1 className="text-xl font-heading">Post not found</h1>
        <p className="text-sm text-muted-foreground">
          This thread may have been deleted.
        </p>
        <Link to="/community" className="text-secondary hover:underline">
          Back to Community →
        </Link>
      </div>
    );
  }

  const user = post.user || {};
  const initials = (user.username || user.stx_address || "S").slice(0, 2).toUpperCase();
  const displayName = user.username || (user.stx_address ? `user_${user.stx_address.slice(0, 8)}` : "STXOSHI user");
  const handle = user.stx_address ? `@${user.stx_address.slice(0, 8)}…` : "@stxoshi";

  return (
    <div className="min-h-screen bg-background">
      {/* Public header — no wallet connect. */}
      <header className="border-b border-border/40 px-4 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="font-heading font-bold tracking-wider text-orange-400"
          data-testid="post-share-brand"
        >
          STXOSHI
        </Link>
        <Link to="/community" data-testid="post-share-community-cta">
          <Button size="sm" variant="outline">
            View community
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </header>

      <main className="p-4 md:p-6">
        <div className="max-w-2xl mx-auto space-y-4">
          <Card className="glass-card border-border" data-testid="post-share-hero">
            <CardContent className="p-6">
              {/* Author row */}
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="w-11 h-11">
                  {user.avatar_url ? <AvatarImage src={user.avatar_url} /> : null}
                  <AvatarFallback className="bg-orange-500/20 text-orange-400 font-heading">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-heading font-semibold truncate" data-testid="post-author-name">
                    {displayName}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    <span className="truncate">{handle}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1 font-mono">
                      <Clock className="w-3 h-3" />
                      {fmtTime(post.created_at)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Content */}
              <div
                className="whitespace-pre-wrap text-[15px] leading-relaxed mb-4"
                data-testid="post-content"
              >
                {post.content}
              </div>

              {/* Optional image */}
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt="Post attachment"
                  className="rounded-lg max-h-96 w-full object-cover mb-4"
                  data-testid="post-image"
                />
              )}

              {/* Engagement counts (read-only on public share view) */}
              <div className="flex items-center gap-6 text-muted-foreground text-sm border-t border-border pt-4">
                <span className="flex items-center gap-1" data-testid="post-replies-count">
                  <MessageSquare className="w-4 h-4" />
                  {post.replies_count || 0}
                </span>
                <span className="flex items-center gap-1" data-testid="post-reposts-count">
                  <Repeat2 className="w-4 h-4" />
                  {post.reposts_count || 0}
                </span>
                <span className="flex items-center gap-1" data-testid="post-likes-count">
                  <Heart className="w-4 h-4" />
                  {post.likes_count || 0}
                </span>
              </div>

              {/* Share actions */}
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyLink}
                  data-testid="post-copy-link-btn"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy link
                </Button>
                <Button
                  size="sm"
                  onClick={shareToX}
                  data-testid="post-share-to-x-btn"
                  className="bg-black text-white hover:bg-neutral-900"
                >
                  <Share2 className="w-3 h-3 mr-1" />
                  Share on X
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="border-orange-500/30 bg-orange-500/5">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                Join the conversation on STXOSHI
              </p>
              <Link to="/community" data-testid="post-share-bottom-cta">
                <Button>
                  Open community
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
